import {
  Box,
  Flex,
  FlexProps,
  Image,
  Skeleton,
  Text,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react'
import React, { useState } from 'react'
import { CLIENT_CONFIG } from '../core/client/buildConfig'
import type { ClientConfig } from '../core/client/ClientProvider'
import { useAgents } from '../hooks/agents/useAgents'
import { useHasNoAccessScopes } from '../hooks/auth/useHasNoAccessScopes'
import { useLanguage } from '../hooks/utility/useLanguage'
import { Banner } from './Banner'
import { markdownToPlainText } from '../core/parsers/markdownParser'

type ChatHeaderProps = FlexProps & { isSidebarOpen?: boolean }

const ChatHeader: React.FC<ChatHeaderProps> = ({ isSidebarOpen = true, ...props }) => {
  const { currentAgent } = useAgents()
  const { t, currentLanguage } = useLanguage()
  const { hasNoAccessScopes } = useHasNoAccessScopes()

  const branding = (CLIENT_CONFIG as ClientConfig).theme.branding
  const customTexts = (CLIENT_CONFIG as ClientConfig).theme.customTexts
  const supportEmail = branding.supportEmail

  // Color mode values
  const headerLogoUrl = useColorModeValue(branding.logoUrl, branding.logoDarkModeUrl ?? branding.logoUrl)
  const headerHoverColor = useColorModeValue('gray.600', 'gray.300')

  const noAccessMessage =
    customTexts.noAccessMessage?.[currentLanguage] ??
    customTexts.noAccessMessage?.en ??
    (supportEmail ? t('errors.noAccessScopesWithEmail', { email: supportEmail }) : t('errors.noAccessScopes'))

  // The no-access banner is dismissible; it eats a lot of vertical space on
  // mobile. Dismissal is session-scoped (survives chat switches and reloads
  // within the tab, reappears on a fresh session) since the message is just an
  // informational prompt to contact support, not a one-time acknowledgement.
  const [isNoAccessBannerDismissed, setIsNoAccessBannerDismissed] = useState(
    () => sessionStorage.getItem('no_access_banner_dismissed') === 'true',
  )
  const dismissNoAccessBanner = () => {
    sessionStorage.setItem('no_access_banner_dismissed', 'true')
    setIsNoAccessBannerDismissed(true)
  }

  return (
    <Box {...props} flex='0 0 auto' position='relative'>
      <Flex
        as='header'
        h='60px'
        align='center'
        justify='center'
        bg='chat.canvas'
        px={6}
        py={3}
        position='relative'
        overflow='hidden'
      >
        {branding.showLogoInHeader && (
          <Image
            src={headerLogoUrl}
            alt={branding.companyName}
            position='absolute'
            // When the sidebar is collapsed a floating toggle button overlays the top-left
            // corner, so shift the logo right to clear it.
            left={isSidebarOpen ? 4 : '52px'}
            top='50%'
            transform='translateY(-50%)'
            h={branding.logoHeight ?? '52px'}
            objectFit='contain'
          />
        )}
        {!currentAgent ? (
          <Skeleton height='20px' width='140px' borderRadius='md' />
        ) : (
          <Tooltip
            label={currentAgent?.description ? markdownToPlainText(currentAgent.description) : undefined}
            hasArrow
            placement='bottom'
            isDisabled={!currentAgent?.description}
            bg='gray.700'
            color='white'
            fontSize='sm'
            maxW='300px'
            textAlign='center'
          >
            <Text
              fontSize='lg'
              fontWeight='bold'
              fontFamily='body'
              color='chat.muted'
              cursor={currentAgent?.description ? 'help' : 'default'}
              _hover={currentAgent?.description ? { color: headerHoverColor } : {}}
              transition='color 0.2s'
            >
              {currentAgent?.name ?? 'Unknown Agent'}
            </Text>
          </Tooltip>
        )}

        {/* Label "this agent can search the internet", hidden on purpose. To show it, restore this
            block with `const isInternetSearchEnabled = Boolean(currentAgent?.tools?.includes('INTERNET_SEARCH'))`
            and the imports HStack, Tag, TagLabel (@chakra-ui/react) and IconWorld (@tabler/icons-react).
        {isInternetSearchEnabled && (
          <HStack position='absolute' right={4} top={3} spacing={2}>
            <Tooltip label={t('internetSearch.badgeTooltip')} hasArrow placement='bottom'>
              <Tag size='md' variant='subtle' colorScheme='yellow'>
                <IconWorld size={16} style={{ marginRight: 6 }} />
                <TagLabel>{t('internetSearch.badgeLabel')}</TagLabel>
              </Tag>
            </Tooltip>
          </HStack>
        )}
        */}
      </Flex>

      {hasNoAccessScopes && !isNoAccessBannerDismissed && (
        <Banner message={noAccessMessage} onClose={dismissNoAccessBanner} closeLabel={t('common.close')} />
      )}

    </Box>
  )
}

export default React.memo(ChatHeader)
